import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { enhancedApi } from '../services/api';
import { AssignmentModal } from '../components/AssignmentModal';
import { Pagination } from '../components/Pagination';
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { 
  FileText, 
  Search,
  Filter,
  Download,
  Eye,
  Edit,
  RefreshCw,
  Cloud,
  Upload,
  Settings,
  Plus,
  Users,
  Activity,
  Stethoscope,
  Calendar,
  AlertCircle,
  TrendingUp,
  UserCheck,
  X,
  ChevronDown,
  Save,
  Check,
  X as XIcon,
  Trash2
} from 'lucide-react';

interface MorbidityCase {
  id: number;
  patient_id: string;
  incident_date: string;
  case_summary: string;
  morbidity_category_id: number;
  audit_indication_id: number;
  department_id?: number;
  unit_id?: number;
  department?: string; // For display purposes
  unit?: string; // For display purposes
  morbidity_category?: string; // For display purposes
  audit_indication?: string; // For display purposes
  audit_status: string;
  computed_audit_status: string;
  is_audit_required: boolean;
  severity?: string;
  age?: string;
  age_unit?: string;
  gender?: string;
  created_by?: number;
  created_by_name?: string; // For display purposes
  created_at?: string;
  updated_at?: string;
}

interface MorbidityCategory {
  id: number;
  name: string;
  description?: string;
  is_active: number;
}

interface MorbidityAuditIndication {
  id: number;
  name: string;
  description?: string;
  is_active: number;
}

export const MorbidityList: React.FC = () => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [morbidityCases, setMorbidityCases] = useState<MorbidityCase[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCases, setSelectedCases] = useState<number[]>([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCase, setSelectedCase] = useState<MorbidityCase | null>(null);
  
  // Inline editing states
  const [editingAuditRequired, setEditingAuditRequired] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState<boolean | null>(null);
  
  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    fromMonth: null as number | null, // No date filter by default
    fromYear: null as number | null, // No date filter by default
    toMonth: null as number | null, // No date filter by default
    toYear: null as number | null, // No date filter by default
    department: '',
    unit: '',
    status: '',
    severity: ''
  });
  const [departments, setDepartments] = useState<any[]>([]);
  const [units, setUnits] = useState<any[]>([]);
  const [morbidityCategories, setMorbidityCategories] = useState<MorbidityCategory[]>([]);
  const [auditIndications, setAuditIndications] = useState<MorbidityAuditIndication[]>([]);
  
  // Assignment modal states
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [selectedCasesForAssignment, setSelectedCasesForAssignment] = useState<number[]>([]);
  
  // Delete confirmation dialog states
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [caseToDelete, setCaseToDelete] = useState<MorbidityCase | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchMorbidityCases();
    fetchDepartmentsAndUnits();
    fetchMorbidityCategoriesAndIndications();
  }, []);

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when filters change
    fetchMorbidityCases();
  }, [filters, searchTerm]);

  useEffect(() => {
    fetchMorbidityCases();
  }, [currentPage, itemsPerPage]);

  const fetchDepartmentsAndUnits = async () => {
    try {
      const [departmentsResponse, unitsResponse] = await Promise.all([
        enhancedApi.departments.getDepartments(),
        enhancedApi.units.getUnits()
      ]);

      if (departmentsResponse.success) {
        setDepartments(departmentsResponse.data);
      }
      if (unitsResponse.success) {
        setUnits(unitsResponse.data);
      }
    } catch (error) {
      console.error('Failed to fetch departments and units:', error);
    }
  };

  const fetchMorbidityCategoriesAndIndications = async () => {
    try {
      const [categoriesResponse, indicationsResponse] = await Promise.all([
        enhancedApi.morbidityCategories.getCategories(),
        enhancedApi.morbidityAuditIndications.getIndications()
      ]);
      
      if (categoriesResponse.success) {
        setMorbidityCategories(categoriesResponse.data);
      }
      
      if (indicationsResponse.success) {
        setAuditIndications(indicationsResponse.data);
      }
    } catch (error) {
      console.error('Failed to fetch morbidity categories and audit indications:', error);
    }
  };

  const fetchMorbidityCases = async () => {
    try {
      setLoading(true);
      // Fetch morbidity cases from the backend API
      const response = await enhancedApi.patients.getPatients({ 
        case_type: 'morbidity',
        page: currentPage,
        limit: itemsPerPage
      });
      if (response.success) {
        // Apply filters to the fetched data
        const filteredCases = applyFilters(response.patients || []);
        setMorbidityCases(filteredCases);
        
        // Update pagination info
        console.log('API Response:', response);
        const responseWithPagination = response as any;
        console.log('Pagination data:', responseWithPagination.pagination);
        if (responseWithPagination.pagination) {
          setTotalPages(responseWithPagination.pagination.pages);
          setTotalItems(responseWithPagination.pagination.total);
          console.log('Set pagination - Pages:', responseWithPagination.pagination.pages, 'Total:', responseWithPagination.pagination.total);
        }
        console.log('Fetched morbidity cases:', filteredCases);
      } else {
        console.error('Failed to fetch morbidity cases:', response.message);
        setMorbidityCases([]);
      }
    } catch (error) {
      console.error('Failed to fetch morbidity cases:', error);
      setMorbidityCases([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (casesData: MorbidityCase[]) => {
    // Debug: Log filter state
    if (filters.fromMonth !== null || filters.fromYear !== null || filters.toMonth !== null || filters.toYear !== null) {
      console.log('Date filters active:', {
        fromMonth: filters.fromMonth,
        fromYear: filters.fromYear,
        toMonth: filters.toMonth,
        toYear: filters.toYear
      });
    }
    
    return casesData.filter(case_ => {
      // Date range filter - allow partial filters (from only, to only, or both)
      const dateString = case_.incident_date ?? case_.created_at;
      if (!dateString) {
        // If no date available, skip date filtering for this record
        return true;
      }
      
      const caseDate = new Date(dateString);
      // Check if date is valid
      if (isNaN(caseDate.getTime())) {
        console.warn('Invalid date for case:', case_.patient_id, 'date string:', dateString);
        // Invalid date, skip date filtering for this record
        return true;
      }
      
      // Smart auto-behavior: Auto-complete date range when only one side is set
      let fromDate: Date | null = null;
      let toDate: Date | null = null;
      
      // Determine "from" date
      if (filters.fromMonth !== null && filters.fromYear !== null) {
        fromDate = new Date(filters.fromYear, filters.fromMonth, 1);
        fromDate.setHours(0, 0, 0, 0);
      }
      
      // Determine "to" date
      if (filters.toMonth !== null && filters.toYear !== null) {
        toDate = new Date(filters.toYear, filters.toMonth + 1, 0); // Last day of toMonth
        toDate.setHours(23, 59, 59, 999);
      }
      
      // Smart auto-complete logic:
      // If only "From" is set → treat as "only that month" (auto-set To to end of that month)
      if (fromDate !== null && toDate === null) {
        toDate = new Date(filters.fromYear!, filters.fromMonth! + 1, 0); // Last day of fromMonth
        toDate.setHours(23, 59, 59, 999);
      }
      
      // If only "To" is set → treat as "up to end of that month" (auto-set From to start of that month)
      if (fromDate === null && toDate !== null) {
        fromDate = new Date(filters.toYear!, filters.toMonth!, 1); // First day of toMonth
        fromDate.setHours(0, 0, 0, 0);
      }
      
      // Apply date filters if we have valid date range
      if (fromDate !== null || toDate !== null) {
        const caseDateCopy = new Date(caseDate);
        
        // Check "from" date filter
        if (fromDate !== null) {
          const caseDateStart = new Date(caseDateCopy);
          caseDateStart.setHours(0, 0, 0, 0);
          if (caseDateStart < fromDate) {
          return false;
          }
        }
        
        // Check "to" date filter
        if (toDate !== null) {
          const caseDateEnd = new Date(caseDateCopy);
          caseDateEnd.setHours(23, 59, 59, 999);
          if (caseDateEnd > toDate) {
            return false;
          }
        }
      }

      // Department filter
      if (filters.department && case_.department !== filters.department) {
        return false;
      }

      // Unit filter
      if (filters.unit && case_.unit_id?.toString() !== filters.unit) {
        return false;
      }

      // Status filter - use computed status
      if (filters.status && case_.computed_audit_status !== filters.status) {
        return false;
      }

      // Severity filter
      if (filters.severity && case_.morbidity_category !== filters.severity) {
        return false;
      }

      // Search term filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          case_.patient_id.toLowerCase().includes(searchLower) ||
          case_.case_summary.toLowerCase().includes(searchLower) ||
          (case_.morbidity_category?.toLowerCase().includes(searchLower) || false) ||
          (case_.audit_indication?.toLowerCase().includes(searchLower) || false)
        );
      }

      return true;
    });
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => {
      const newFilters = {
      ...prev,
      [key]: value
      };
      
      // Debug: Log filter changes
      console.log('Filter change:', { key, value, previous: prev, newFilters });
      
      // Validate date range: ensure "from" <= "to" (only when both from and to are complete)
      if (newFilters.fromMonth !== null && newFilters.fromYear !== null &&
          newFilters.toMonth !== null && newFilters.toYear !== null) {
        const fromDate = new Date(newFilters.fromYear, newFilters.fromMonth, 1);
        const toDate = new Date(newFilters.toYear, newFilters.toMonth + 1, 0);
        
        if (fromDate > toDate) {
          // If invalid range, reset the "to" date when setting "from", or reset "from" when setting "to"
          if (key === 'fromMonth' || key === 'fromYear') {
            newFilters.toMonth = null;
            newFilters.toYear = null;
          } else if (key === 'toMonth' || key === 'toYear') {
            newFilters.fromMonth = null;
            newFilters.fromYear = null;
          }
        }
      }
      
      return newFilters;
    });
  };

  const clearFilters = () => {
    setFilters({
      fromMonth: null,
      fromYear: null,
      toMonth: null,
      toYear: null,
      department: '',
      unit: '',
      status: '',
      severity: ''
    });
    setSearchTerm('');
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.department) count++;
    if (filters.unit) count++;
    if (filters.status) count++;
    if (filters.severity) count++;
    if (searchTerm) count++;
    return count;
  };

  const handleCaseSelection = (caseId: number) => {
    setSelectedCases(prev => 
      prev.includes(caseId) 
        ? prev.filter(id => id !== caseId)
        : [...prev, caseId]
    );
  };

  const handleSelectAll = () => {
    if (!Array.isArray(morbidityCases)) return;
    
    if (selectedCases.length === morbidityCases.length) {
      setSelectedCases([]);
    } else {
      setSelectedCases(morbidityCases.map(c => c.id));
    }
  };

  const handleAddCase = () => {
    setShowAddModal(true);
  };

  const handleSaveCase = async (newCase: Omit<MorbidityCase, 'id'>) => {
    try {
      setLoading(true);
      
      // Call the backend API to create the morbidity case
      const response = await enhancedApi.patients.createMorbidityCase(newCase);
      
      if (response.success) {
        // Refresh the list to get the updated data from the database
        await fetchMorbidityCases();
        setShowAddModal(false);
        console.log('Morbidity case created successfully:', response.data);
      } else {
        console.error('Failed to create morbidity case:', response.message);
        addToast({
          type: 'error',
          title: 'Creation Failed',
          message: `Failed to create morbidity case: ${response.message}`
        });
      }
    } catch (error) {
      console.error('Error creating morbidity case:', error);
      addToast({
        type: 'error',
        title: 'Creation Failed',
        message: 'Error creating morbidity case. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewCase = (case_: MorbidityCase) => {
    setSelectedCase(case_);
    setShowViewModal(true);
  };

  const handleEditCase = (case_: MorbidityCase) => {
    setSelectedCase(case_);
    setShowEditModal(true);
  };

  // Inline editing functions
  const handleStartEditAuditRequired = (case_: MorbidityCase) => {
    setEditingAuditRequired(case_.id);
    setEditingValue(case_.is_audit_required);
  };

  const handleSaveAuditRequired = async (case_: MorbidityCase) => {
    try {
      const response = await enhancedApi.admin.updateMorbidityCaseAuditRequired(case_.id, editingValue!);
      if (response.success) {
        // Update the case in the local state
        setMorbidityCases(prevCases => 
          prevCases.map(c => 
            c.id === case_.id 
              ? { 
                  ...c, 
                  is_audit_required: editingValue!
                  // Note: audit_status is now computed from assignment table, no need to update it here
                }
              : c
          )
        );
        setEditingAuditRequired(null);
        setEditingValue(null);
      } else {
        addToast({
          type: 'error',
          title: 'Update Failed',
          message: `Failed to update audit required status: ${response.message}`
        });
      }
    } catch (error) {
      console.error('Failed to update audit required status:', error);
      addToast({
        type: 'error',
        title: 'Update Failed',
        message: 'Failed to update audit required status. Please try again.'
      });
    }
  };

  const handleCancelEditAuditRequired = () => {
    setEditingAuditRequired(null);
    setEditingValue(null);
  };

  const handleUpdateCase = async (updatedCase: MorbidityCase) => {
    try {
      setLoading(true);
      
      // Add case_type to the update data so backend knows which table to update
      const updateData = {
        ...updatedCase,
        case_type: 'morbidity'
      };
      
      // Call the backend API to update the morbidity case
      const response = await enhancedApi.patients.updatePatient(updatedCase.id, updateData);
      
      if (response.success) {
        // Refresh the list to get the updated data from the database
        await fetchMorbidityCases();
        setShowEditModal(false);
        setSelectedCase(null);
        addToast({
          type: 'success',
          title: 'Case Updated',
          message: 'Morbidity case updated successfully'
        });
      } else {
        addToast({
          type: 'error',
          title: 'Update Failed',
          message: `Failed to update morbidity case: ${response.message}`
        });
      }
    } catch (error) {
      console.error('Error updating morbidity case:', error);
      addToast({
        type: 'error',
        title: 'Update Failed',
        message: 'Error updating morbidity case. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  // Assignment functions
  const handleAssignAudit = () => {
    if (selectedCases.length === 0) {
      addToast({
        type: 'warning',
        title: 'No Cases Selected',
        message: 'Please select at least one case to assign'
      });
      return;
    }
    setSelectedCasesForAssignment(selectedCases);
    setShowAssignmentModal(true);
  };

  const handleAssignmentComplete = () => {
    setSelectedCases([]);
    fetchMorbidityCases(); // Refresh the list
  };

  // Delete functions
  const handleDeleteClick = (case_: MorbidityCase) => {
    setCaseToDelete(case_);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!caseToDelete) return;
    
    try {
      setIsDeleting(true);
      const response = await enhancedApi.admin.deleteMorbidityCase(caseToDelete.id);
      
      if (response.success) {
        addToast({
          type: 'success',
          title: 'Case Deleted',
          message: 'Morbidity case deleted successfully'
        });
        setShowDeleteDialog(false);
        setCaseToDelete(null);
        await fetchMorbidityCases(); // Refresh the list
      } else {
        addToast({
          type: 'error',
          title: 'Delete Failed',
          message: response.message || 'Failed to delete morbidity case'
        });
      }
    } catch (error) {
      console.error('Error deleting morbidity case:', error);
      addToast({
        type: 'error',
        title: 'Delete Failed',
        message: 'Error deleting morbidity case. Please try again.'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteDialog(false);
    setCaseToDelete(null);
  };

  // Check if a case can be deleted (not assigned and no audit form)
  const canDeleteCase = (case_: MorbidityCase) => {
    // Can delete if status is 'unassigned' or 'not_required' (meaning no assignment and no form)
    return case_.computed_audit_status === 'unassigned' || case_.computed_audit_status === 'not_required';
  };

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const getSeverityBadge = (severity: string) => {
    const severityClasses = {
      low: 'badge badge-success',
      medium: 'badge badge-warning',
      high: 'badge badge-error',
      critical: 'badge badge-error'
    };
    
    return (
      <span className={severityClasses[severity as keyof typeof severityClasses] || 'badge badge-secondary'}>
        {severity.toUpperCase()}
      </span>
    );
  };

  const getStatusBadge = (status: string) => {
    const statusClasses = {
      pending: 'badge badge-warning',
      assigned: 'badge badge-info',
      in_progress: 'badge badge-primary',
      submitted: 'badge badge-success',
      completed: 'badge badge-success',
      approved: 'badge badge-success',
      rejected: 'badge badge-error',
      not_required: 'badge badge-secondary',
      unassigned: 'badge badge-warning'
    };
    
    return (
      <span className={statusClasses[status as keyof typeof statusClasses] || 'badge badge-secondary'}>
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const filteredCases = Array.isArray(morbidityCases) ? morbidityCases.filter(case_ => {
    // Search filter
    const matchesSearch = !searchTerm || (
      case_.patient_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      case_.case_summary?.toLowerCase().includes(searchTerm.toLowerCase()) ||    
      case_.morbidity_category?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    // Status filter - use computed status
    const matchesStatus = !filters.status || case_.computed_audit_status === filters.status;
    
    // Severity filter
    const matchesSeverity = !filters.severity || case_.severity === filters.severity;
    
    return matchesSearch && matchesStatus && matchesSeverity;
  }) : [];

  return (
    <div className="space-y-8">
      {/* Enhanced Header */}
      <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 px-8 py-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Morbidity List</h1>
              <p className="text-blue-100">Comprehensive morbidity case management - Manual entry only</p>
            </div>
          </div>
        </div>
      </div>

      {/* Morbidity Cases */}
        <div className="space-y-6">
          {/* Case Actions */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search morbidity cases..."
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <button 
                  className={`btn btn-outline relative ${getActiveFiltersCount() > 0 ? 'bg-blue-50 border-blue-300 text-blue-700' : ''}`}
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <Filter className="h-4 w-4" />
                  Filters
                  {getActiveFiltersCount() > 0 && (
                    <span className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {getActiveFiltersCount()}
                    </span>
                  )}
                </button>
              </div>
              <div className="flex gap-3">
                <button className="btn btn-outline">
                  <Download className="h-4 w-4" />
                  Export
                </button>
                {(user?.role === 'superuser' || user?.role === 'reviewer') && (
                  <button 
                    className="btn btn-primary"
                    onClick={handleAssignAudit}
                    disabled={selectedCases.length === 0}
                  >
                    <Plus className="h-4 w-4" />
                    Assign Audit ({selectedCases.length})
                  </button>
                )}
                {user?.role === 'superuser' && (
                  <button className="btn btn-secondary" onClick={handleAddCase}>
                    <Plus className="h-4 w-4" />
                    Add Morbidity Case
                  </button>
                )}
              </div>
            </div>

            {/* Advanced Filters Panel */}
            {showFilters && (
              <div className="bg-gray-50 rounded-xl p-6 mb-6 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Advanced Filters</h3>
                  <button
                    onClick={clearFilters}
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    <X className="h-4 w-4" />
                    Clear All
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Date Range - From */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">From Period</label>
                    <div className="flex gap-2">
                      <select
                        value={filters.fromMonth ?? ''}
                        onChange={(e) => handleFilterChange('fromMonth', e.target.value ? parseInt(e.target.value) : null)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">All Months</option>
                        <option value={0}>January</option>
                        <option value={1}>February</option>
                        <option value={2}>March</option>
                        <option value={3}>April</option>
                        <option value={4}>May</option>
                        <option value={5}>June</option>
                        <option value={6}>July</option>
                        <option value={7}>August</option>
                        <option value={8}>September</option>
                        <option value={9}>October</option>
                        <option value={10}>November</option>
                        <option value={11}>December</option>
                      </select>
                      <select
                        value={filters.fromYear ?? ''}
                        onChange={(e) => {
                          const yearValue = e.target.value;
                          const parsedYear = yearValue && yearValue !== '' ? parseInt(yearValue, 10) : null;
                          console.log('Year selected:', { yearValue, parsedYear });
                          handleFilterChange('fromYear', parsedYear);
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">All Years</option>
                        {Array.from({ length: 6 }, (_, i) => {
                          const year = new Date().getFullYear() - i;
                          return (
                            <option key={year} value={year}>{year}</option>
                          );
                        })}
                      </select>
                    </div>
                  </div>

                  {/* Date Range - To */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">To Period</label>
                    <div className="flex gap-2">
                      <select
                        value={filters.toMonth ?? ''}
                        onChange={(e) => handleFilterChange('toMonth', e.target.value ? parseInt(e.target.value) : null)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">All Months</option>
                        <option value={0}>January</option>
                        <option value={1}>February</option>
                        <option value={2}>March</option>
                        <option value={3}>April</option>
                        <option value={4}>May</option>
                        <option value={5}>June</option>
                        <option value={6}>July</option>
                        <option value={7}>August</option>
                        <option value={8}>September</option>
                        <option value={9}>October</option>
                        <option value={10}>November</option>
                        <option value={11}>December</option>
                      </select>
                      <select
                        value={filters.toYear ?? ''}
                        onChange={(e) => {
                          const yearValue = e.target.value;
                          const parsedYear = yearValue && yearValue !== '' ? parseInt(yearValue, 10) : null;
                          handleFilterChange('toYear', parsedYear);
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">All Years</option>
                        {Array.from({ length: 6 }, (_, i) => {
                          const year = new Date().getFullYear() - i;
                          return (
                            <option key={year} value={year}>{year}</option>
                          );
                        })}
                      </select>
                    </div>
                  </div>

                  {/* Department Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
                    <select
                      value={filters.department}
                      onChange={(e) => handleFilterChange('department', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">All Departments</option>
                      {departments.filter(d => d.is_active).map(dept => (
                        <option key={dept.id} value={dept.name}>{dept.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Unit Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Unit</label>
                    <select
                      value={filters.unit}
                      onChange={(e) => handleFilterChange('unit', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">All Units</option>
                      {units.filter(u => u.is_active).map(unit => (
                        <option key={unit.id} value={unit.emr_code}>{unit.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                  {/* Status Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Audit Status</label>
                    <select
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">All Statuses</option>
                      <option value="pending">Pending</option>
                      <option value="assigned">Assigned</option>
                      <option value="in_progress">In Progress</option>
                      <option value="submitted">Submitted</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                      <option value="not_required">Not Required</option>
                    </select>
                  </div>

                  {/* Severity Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Severity</label>
                    <select
                      value={filters.severity}
                      onChange={(e) => handleFilterChange('severity', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">All Severities</option>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>

                </div>
              </div>
            )}

            {/* Case Table */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                <span className="ml-3 text-gray-600">Loading morbidity cases...</span>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          checked={Array.isArray(morbidityCases) && selectedCases.length === morbidityCases.length && morbidityCases.length > 0}
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th>Incident Date</th>
                      <th>Patient ID</th>
                      <th>Case Summary</th>
                      <th>Morbidity Category</th>
                      <th>Audit Indication</th>
                      <th>Audit Status</th>
                      <th>Created By</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {morbidityCases.length > 0 ? morbidityCases.map((case_) => (
                      <tr key={case_.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedCases.includes(case_.id)}
                            onChange={() => handleCaseSelection(case_.id)}
                          />
                        </td>
                        <td>
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                            {formatDate(case_.incident_date)}
                          </div>
                        </td>
                        <td>
                          <div className="font-medium">{case_.patient_id}</div>
                        </td>
                        <td>
                          <div className="max-w-xs truncate" title={case_.case_summary}>
                            {case_.case_summary}
                          </div>
                        </td>
                        <td>
                          <div className="max-w-xs truncate" title={case_.morbidity_category}>
                            {case_.morbidity_category}
                          </div>
                        </td>
                        <td>
                          <div className="max-w-xs truncate" title={case_.audit_indication}>
                            {case_.audit_indication}
                          </div>
                        </td>
                        <td>
                          {getStatusBadge(case_.computed_audit_status)}
                        </td>
                        <td>
                          <div className="text-sm text-gray-600">
                            {case_.created_by_name || `User ${case_.created_by}`}
                          </div>
                        </td>
                        <td>
                          <div className="flex gap-2">
                            <button 
                              className="btn btn-sm btn-outline"
                              onClick={() => handleViewCase(case_)}
                              title="View case details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            {user?.role === 'superuser' && (
                              <>
                              <button 
                                className="btn btn-sm btn-primary"
                                onClick={() => handleEditCase(case_)}
                                title="Edit case"
                              >
                                <Edit className="h-4 w-4" />
                                </button>
                                {canDeleteCase(case_) && (
                                  <button 
                                    className="btn btn-sm btn-error"
                                    onClick={() => handleDeleteClick(case_)}
                                    title="Delete case"
                                  >
                                    <Trash2 className="h-4 w-4" />
                              </button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={10} className="text-center py-8 text-gray-500">
                          {loading ? 'Loading morbidity cases...' : 'No morbidity cases found'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Pagination */}
            {morbidityCases.length > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                showItemsPerPage={true}
                itemsPerPageOptions={[10, 20, 50, 100]}
              />
            )}
          </div>
        </div>

      {/* Add Case Modal */}
      {showAddModal && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-2xl mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Add New Morbidity Case</h3>
              <button 
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target as HTMLFormElement);
              const newCase: Omit<MorbidityCase, 'id'> = {
                patient_id: formData.get('patient_id') as string,
                incident_date: formData.get('incident_date') as string,
                case_summary: formData.get('case_summary') as string,
                morbidity_category_id: parseInt(formData.get('morbidity_category_id') as string),
                audit_indication_id: parseInt(formData.get('audit_indication_id') as string),
                department_id: departments.find(d => d.name === formData.get('department'))?.id,
                unit_id: parseInt(formData.get('unit') as string) || undefined,
                audit_status: 'pending',
                computed_audit_status: 'pending',
                is_audit_required: true,
                age: formData.get('age') as string,
                age_unit: formData.get('age_unit') as string,
                gender: formData.get('gender') as string || undefined
              };
              handleSaveCase(newCase);
            }}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="form-label">Patient ID</label>
                  <input type="text" name="patient_id" className="form-input" required />
                </div>
                <div>
                  <label className="form-label">Age</label>
                  <div className="flex gap-2">
                    <input 
                      type="number" 
                      name="age" 
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent flex-1 min-w-0" 
                      required 
                    />
                    <select 
                      name="age_unit" 
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-28" 
                      defaultValue="years"
                    >
                      <option value="hours">hours</option>
                      <option value="days">days</option>
                      <option value="months">months</option>
                      <option value="years">years</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="form-label">Gender</label>
                  <select name="gender" className="form-select" required>
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">Department</label>
                  <select name="department" className="form-select" required>
                    <option value="">Select Department</option>
                    {departments.map(dept => (
                      <option key={dept.id} value={dept.name}>{dept.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Unit</label>
                  <select name="unit" className="form-select" required>
                    <option value="">Select Unit</option>
                    {units.map(unit => (
                      <option key={unit.id} value={unit.emr_code}>{unit.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Date of Incident</label>
                  <input type="datetime-local" name="incident_date" className="form-input" required />
                </div>
                <div>
                  <label className="form-label">Morbidity Category</label>
                  <select name="morbidity_category_id" className="form-select" required>
                    <option value="">Select Category</option>
                    {morbidityCategories.map(category => (
                      <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Indication to Audit</label>
                  <select name="audit_indication_id" className="form-select" required>
                    <option value="">Select Indication</option>
                    {auditIndications.map(indication => (
                      <option key={indication.id} value={indication.id}>{indication.name}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="form-label">Case Summary</label>
                  <textarea name="case_summary" className="form-input" rows={4} required 
                    placeholder="Provide a detailed summary of the morbidity case, including what happened, contributing factors, and any relevant details..."></textarea>
                </div>
              </div>
              
              <div className="flex justify-end gap-3">
                <button 
                  type="button" 
                  onClick={() => setShowAddModal(false)}
                  className="btn btn-outline"
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Case
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* View Case Modal */}
      {showViewModal && selectedCase && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-2xl mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Morbidity Case Details</h3>
              <button 
                onClick={() => setShowViewModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Patient ID</label>
                  <p className="text-gray-900">{selectedCase.patient_id}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
                  <p className="text-gray-900">{selectedCase.age}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                  <p className="text-gray-900">{selectedCase.gender}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Incident Date</label>
                  <p className="text-gray-900">{formatDate(selectedCase.incident_date)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                  <p className="text-gray-900">{selectedCase.department}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                  <p className="text-gray-900">{selectedCase.unit || selectedCase.unit_id}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Morbidity Category</label>
                  <p className="text-gray-900">{selectedCase.morbidity_category}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Audit Indication</label>
                  <p className="text-gray-900">{selectedCase.audit_indication}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Audit Status</label>
                  <div className="mt-1">
                    {getStatusBadge(selectedCase.computed_audit_status)}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Created By</label>
                  <p className="text-gray-900">{selectedCase.created_by_name || `User ${selectedCase.created_by}`}</p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Case Summary</label>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-900 whitespace-pre-wrap">{selectedCase.case_summary}</p>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button 
                onClick={() => setShowViewModal(false)}
                className="btn btn-outline"
              >
                Close
              </button>
              {user?.role === 'superuser' && (
                <button 
                  onClick={() => {
                    setShowViewModal(false);
                    handleEditCase(selectedCase);
                  }}
                  className="btn btn-primary"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Case
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Edit Case Modal */}
      {showEditModal && selectedCase && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-2xl mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Edit Morbidity Case</h3>
              <button 
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target as HTMLFormElement);
              const updatedCase: MorbidityCase = {
                ...selectedCase,
                patient_id: formData.get('patient_id') as string,
                incident_date: formData.get('incident_date') as string,
                case_summary: formData.get('case_summary') as string,
                morbidity_category_id: parseInt(formData.get('morbidity_category_id') as string),
                audit_indication_id: parseInt(formData.get('audit_indication_id') as string),
                department_id: departments.find(d => d.name === formData.get('department'))?.id,
                unit_id: parseInt(formData.get('unit') as string) || undefined,
                age: formData.get('age') as string,
                age_unit: formData.get('age_unit') as string,
                gender: formData.get('gender') as string || undefined
              };
              handleUpdateCase(updatedCase);
            }}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="form-label">Patient ID</label>
                  <input type="text" name="patient_id" className="form-input" defaultValue={selectedCase.patient_id} required />
                </div>
                <div>
                  <label className="form-label">Age</label>
                  <div className="flex gap-2">
                    <input 
                      type="number" 
                      name="age" 
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent flex-1 min-w-0" 
                      defaultValue={selectedCase.age} 
                      required 
                    />
                    <select 
                      name="age_unit" 
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-28" 
                      defaultValue={selectedCase.age_unit || "years"}
                    >
                      <option value="hours">hours</option>
                      <option value="days">days</option>
                      <option value="months">months</option>
                      <option value="years">years</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="form-label">Gender</label>
                  <select name="gender" className="form-select" defaultValue={selectedCase.gender} required>
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">Department</label>
                  <select name="department" className="form-select" defaultValue={selectedCase.department} required>
                    <option value="">Select Department</option>
                    {departments.map(dept => (
                      <option key={dept.id} value={dept.name}>{dept.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Unit</label>
                  <select name="unit" className="form-select" defaultValue={selectedCase.unit} required>
                    <option value="">Select Unit</option>
                    {units.map(unit => (
                      <option key={unit.id} value={unit.emr_code}>{unit.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Date of Incident</label>
                  <input 
                    type="datetime-local" 
                    name="incident_date" 
                    className="form-input" 
                    defaultValue={selectedCase.incident_date ? new Date(selectedCase.incident_date).toISOString().slice(0, 16) : ''} 
                    required 
                  />
                </div>
                <div>
                  <label className="form-label">Morbidity Category</label>
                  <select name="morbidity_category_id" className="form-select" defaultValue={selectedCase.morbidity_category_id} required>
                    <option value="">Select Category</option>
                    {morbidityCategories.map(category => (
                      <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Indication to Audit</label>
                  <select name="audit_indication_id" className="form-select" defaultValue={selectedCase.audit_indication_id} required>
                    <option value="">Select Indication</option>
                    {auditIndications.map(indication => (
                      <option key={indication.id} value={indication.id}>{indication.name}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="form-label">Case Summary</label>
                  <textarea 
                    name="case_summary" 
                    className="form-input" 
                    rows={4} 
                    defaultValue={selectedCase.case_summary}
                    required 
                    placeholder="Provide a detailed summary of the morbidity case, including what happened, contributing factors, and any relevant details..."
                  ></textarea>
                </div>
              </div>
              
              <div className="flex justify-end gap-3">
                <button 
                  type="button" 
                  onClick={() => setShowEditModal(false)}
                  className="btn btn-outline"
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  <Save className="h-4 w-4 mr-2" />
                  Update Case
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Assignment Modal */}
      <AssignmentModal
        isOpen={showAssignmentModal}
        onClose={() => setShowAssignmentModal(false)}
        selectedCases={selectedCasesForAssignment}
        caseType="morbidity"
        onAssignmentComplete={handleAssignmentComplete}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Morbidity Case"
        message={`Are you sure you want to delete this morbidity case (Patient ID: ${caseToDelete?.patient_id})? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={isDeleting}
      />
    </div>
  );
};
